/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Pressable,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Rect } from 'react-native-svg';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { styles } from './HeroBanner.styles';
import { HeroBanner, HeroBannerData } from '../../models/homepageBlockTypes';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import { useDeviceInfo } from '@airasia-rn/ui';
import { useLogger } from '@airasia-rn/core-logger';
import {
  pd,
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils';
import {
  HomePageEvents,
  trackHeroBannerClicked,
  trackHeroBannerViewed,
} from '../../analytics/homepage';
import HeroBannerBackground from './HeroBannerBackground.web';
import ChevronLeft from '@move-travel/react-native-phoenix-icons/ChevronLeft';
import ChevronRight from '@move-travel/react-native-phoenix-icons/ChevronRight';
import {
  getHeroBannerImageStyle,
  getHeroSlideImageOpacity,
} from './HeroBannerView.web.animation';

const AUTO_SCROLL_INTERVAL = 5000;

const buildBannerAnalyticsLabel = (banner: HeroBanner, index: number) =>
  `${banner.label ?? ''} | ${index + 1}`;

function HeroBannerView({
  heroBannerData,
}: {
  heroBannerData: HeroBannerData;
}) {
  const scrollRef = useRef<ScrollView>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const indexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [hoveredBannerIndex, setHoveredBannerIndex] = useState<number | null>(
    null,
  );
  const { isMobileWidth } = useDeviceInfo();
  const { logEvent } = useLogger();
  const trackedBannerIndexesRef = useRef<Set<number>>(new Set());
  const isFirstScrollRef = useRef(true);

  const bannerCount = heroBannerData.banners.length;

  useEffect(() => {
    if (bannerCount <= 1 || !containerWidth) return;

    // Start auto-scroll. On initial mount, startAutoScroll will use a 15-second delay.
    startAutoScroll();
    return stopAutoScroll;
  }, [bannerCount, containerWidth]);

  const startAutoScroll = () => {
    stopAutoScroll();

    // If it's the very first auto-scroll on mount, wait 10 seconds.
    // Otherwise, use the standard 5-second interval.
    const delay = isFirstScrollRef.current ? 10000 : AUTO_SCROLL_INTERVAL;

    timerRef.current = setTimeout(() => {
      isFirstScrollRef.current = false; // Set to false after the first transition
      handleNext();
    }, delay);
  };

  const stopAutoScroll = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleScrollBeginDrag = () => {
    isFirstScrollRef.current = false; // If the user manually drags, cancel the first LCP delay
    stopAutoScroll();
  };

  const trackBannerView = useCallback(
    (index: number) => {
      const banner = heroBannerData.banners[index];
      if (!banner || trackedBannerIndexesRef.current.has(index)) return;
      trackedBannerIndexesRef.current.add(index);
      const payload = trackHeroBannerViewed({
        label: buildBannerAnalyticsLabel(banner, index),
      });
      logEvent(HomePageEvents.view, payload);
    },
    [heroBannerData, logEvent],
  );

  const scrollToIndex = (nextIndex: number) => {
    const normalizedIndex = (nextIndex + bannerCount) % bannerCount;

    indexRef.current = normalizedIndex;
    setActiveIndex(normalizedIndex);

    scrollRef.current?.scrollTo({
      x: containerWidth * normalizedIndex,
      animated: true,
    });

    trackBannerView(normalizedIndex);
    startAutoScroll();
  };

  const handleNext = () => {
    scrollToIndex(indexRef.current + 1);
  };

  const handlePrev = () => {
    scrollToIndex(indexRef.current - 1);
  };

  const onContainerLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const getNearestIndex = (offset: number) => {
    if (!containerWidth) {
      return 0;
    }

    return Math.round(offset / containerWidth);
  };

  const syncVisibleIndex = (offset: number) => {
    const nextIndex = getNearestIndex(offset);
    setActiveIndex((currentIndex) =>
      currentIndex === nextIndex ? currentIndex : nextIndex,
    );
    return nextIndex;
  };

  const handleBannerPress = (redirectionUrl?: string, index?: number) => {
    const banner =
      typeof index === 'number' ? heroBannerData.banners[index] : undefined;
    if (banner && typeof index === 'number') {
      logEvent(
        HomePageEvents.tap,
        trackHeroBannerClicked({
          label: buildBannerAnalyticsLabel(banner, index),
        }),
      );
    }
    if (!redirectionUrl || typeof window === 'undefined') return;
    window.open(redirectionUrl, '_blank', 'noopener,noreferrer');
  };

  const handleScrollComplete = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    if (!containerWidth) return;
    const offset = event.nativeEvent.contentOffset.x;
    setScrollOffset(offset);
    const nextIndex = syncVisibleIndex(offset);
    indexRef.current = nextIndex;
    trackBannerView(nextIndex);
    startAutoScroll();
  };

  const containerRef = useRef<View>(null);

  useEffect(() => {
    if (!containerWidth || bannerCount === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const node = (containerRef.current as any)?._nativeTag
      ? undefined
      : (containerRef.current as unknown as HTMLElement);
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackBannerView(indexRef.current);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [bannerCount, containerWidth, trackBannerView]);

  if (!heroBannerData || !heroBannerData.banners.length) return null;

  return (
    <View
      ref={containerRef}
      style={styles.container}
      onLayout={onContainerLayout}
    >
      <View style={styles.carouselWrapper}>
        <View
          style={styles.bannerWrapper}
          {...({ className: 'hero-banner-wrapper' } as Record<string, any>)}
        >
          <ScrollView
            ref={scrollRef}
            horizontal
            // pagingEnabled wraps every slide in an auto-width snap View, in
            // which the pre-measure `100%` slide width collapses to 0. Paging
            // only matters once the slides have pixel widths anyway.
            pagingEnabled={!!containerWidth}
            style={StyleSheet.absoluteFill}
            // Before the container is measured (server HTML and the first
            // client render) the slides have no pixel width, so size them to
            // the viewport: the first hero image is then painted from the
            // server HTML instead of appearing after hydration.
            contentContainerStyle={{
              height: '100%',
              ...(!containerWidth && { width: '100%' }),
            }}
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={16}
            onScroll={(event) => {
              const offset = event.nativeEvent.contentOffset.x;
              setScrollOffset(offset);
              syncVisibleIndex(offset);
            }}
            onScrollBeginDrag={handleScrollBeginDrag}
            onScrollEndDrag={handleScrollComplete}
            onMomentumScrollEnd={handleScrollComplete}
          >
            {heroBannerData.banners.map((item, idx) => {
              const hasText = !!(
                item.headline ||
                item.subHeadline ||
                item.promoCode ||
                item.tnc
              );
              const isHovered = !isMobileWidth && hoveredBannerIndex === idx;
              const imageOpacity = getHeroSlideImageOpacity({
                containerWidth,
                index: idx,
                scrollOffset,
              });

              return (
                <Pressable
                  key={idx}
                  disabled={!item.redirectionUrl}
                  onPress={() => handleBannerPress(item.redirectionUrl, idx)}
                  onHoverIn={() => setHoveredBannerIndex(idx)}
                  onHoverOut={() =>
                    setHoveredBannerIndex((currentIndex) =>
                      currentIndex === idx ? null : currentIndex,
                    )
                  }
                  style={[
                    styles.bannerSlide,
                    { width: containerWidth || '100%' },
                  ]}
                >
                  <HeroBannerBackground
                    containerWidth={containerWidth}
                    backgroundImage={item.backgroundImage}
                    imageStyle={getHeroBannerImageStyle({
                      imageOpacity,
                      isHovered,
                    })}
                  >
                    {hasText && (
                      <Svg
                        style={StyleSheet.absoluteFill}
                        width="100%"
                        height="100%"
                      >
                        <Defs>
                          <LinearGradient
                            id={`heroGradient-${idx}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <Stop
                              offset="0%"
                              stopColor={getColor(
                                pd.sys.colour.content_primary,
                                10,
                              )}
                              stopOpacity="1"
                            />
                            <Stop
                              offset="100%"
                              stopColor={getColor(
                                pd.sys.colour.content_primary,
                                80,
                              )}
                              stopOpacity="1"
                            />
                          </LinearGradient>
                        </Defs>

                        <Rect
                          x="0"
                          y="0"
                          width="100%"
                          height="100%"
                          fill={`url(#heroGradient-${idx})`}
                        />
                      </Svg>
                    )}

                    <View style={styles.innerContent}>
                      <View style={styles.textWrapper}>
                        {item.headline ? (
                          <TextView
                            text={item.headline || ''}
                            style={[
                              getTextStyle(
                                pd.sys.typescale.hero_banner,
                                pd.sys.colour.content_reversed,
                              ),
                              styles.title,
                            ]}
                          />
                        ) : null}

                        {item.subHeadline ? (
                          <TextView
                            text={item.subHeadline || ''}
                            style={[
                              getTextStyle(
                                pd.sys.typescale.heading_2,
                                pd.sys.colour.content_reversed,
                              ),
                              styles.subtitle,
                            ]}
                          />
                        ) : null}

                        {item.promoCode ? (
                          <View style={styles.promoBadge}>
                            <TextView
                              text={`Use code: ${item.promoCode}`}
                              style={styles.promoTextStyle}
                            />
                          </View>
                        ) : null}

                        {item.tnc ? (
                          <TextView text={item.tnc} style={styles.tncText} />
                        ) : null}
                      </View>
                    </View>
                  </HeroBannerBackground>
                </Pressable>
              );
            })}
          </ScrollView>

          {bannerCount > 1 ? (
            <View style={styles.paginationOverlay} pointerEvents="none">
              <View style={styles.pagination}>
                {heroBannerData.banners.map((_, dotIdx) => (
                  <View
                    key={dotIdx}
                    style={[
                      styles.dot,
                      dotIdx === activeIndex && styles.activeDot,
                    ]}
                  />
                ))}
              </View>
            </View>
          ) : null}
        </View>

        {bannerCount > 1 && (
          <>
            <Pressable
              style={[
                styles.chevron,
                styles.leftChevron,
                activeIndex > 0 && styles.visible,
              ]}
              onPress={handlePrev}
            >
              <IconView
                height={pd.ref.space.spacing50.px}
                width={pd.ref.space.spacing50.px}
                tint={getColor(pd.sys.colour.content_secondary)}
              >
                <ChevronLeft />
              </IconView>
            </Pressable>

            <Pressable
              style={[
                styles.chevron,
                styles.rightChevron,
                activeIndex < bannerCount - 1 && styles.visible,
              ]}
              onPress={handleNext}
            >
              <IconView
                height={pd.ref.space.spacing50.px}
                width={pd.ref.space.spacing50.px}
                tint={getColor(pd.sys.colour.content_secondary)}
              >
                <ChevronRight />
              </IconView>
            </Pressable>
          </>
        )}
      </View>
    </View>
  );
}

export default HeroBannerView;
